import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusPill, Empty } from "@/components/ui-helpers";

const CONNECTOR_NAMES = {
  zoho: "Zoho CRM", meta: "Meta Ads", typeform: "Typeform", calendly: "Calendly",
  gcal: "Google Calendar", gmail: "Gmail", wati: "Wati", zapier: "Zapier",
  mailerlite: "MailerLite", slack: "Slack",
};

const Kpi = ({ label, value, sub, hint, tone }) => (
  <div className="panel p-3 min-w-0" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    <div className={`text-xl font-medium mt-1 ${tone === "ok" ? "text-[var(--ok)]" : tone === "warn" ? "text-[var(--warn)]" : "text-neutral-100"}`}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-neutral-500 mt-0.5">{sub}</div>}
    {hint && <div className="text-[10px] text-neutral-600 mt-0.5">{hint}</div>}
  </div>
);

export default function Desk() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/desk/overview").then((r) => setData(r.data)).catch((e) => setErr(e.message));
  }, []);

  if (err) return <div className="p-6 text-sm text-red-500">{err}</div>;
  if (!data) return <div className="p-6 text-xs text-neutral-500">Loading…</div>;

  const k = data.kpis;
  const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");

  return (
    <div data-testid="desk-page">
      <PageHeader
        title="Desk"
        sub={`Last updated ${new Date(data.status_line.tasks_updated_at).toLocaleString("en-IN")}`}
        right={
          <>
            <span className="pill">
              Health <span className="font-mono text-neutral-200">{data.status_line.health}/10</span>
            </span>
            <span className={`pill ${data.status_line.sla_breaches ? "pill-breach" : "pill-ok"}`}>
              SLA {data.status_line.sla_breaches} breach
            </span>
            <span className={`pill ${data.status_line.funnel_alerts ? "pill-warn" : "pill-ok"}`}>
              Funnel {data.status_line.funnel_alerts} alerts
            </span>
          </>
        }
      />

      <div className="p-4 md:p-6 space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="kpi-strip">
          <Kpi label="Desk health" value={`${k.desk_health}/10`} tone={k.desk_health >= 8 ? "ok" : "warn"} />
          <Kpi label="Qualified 7d" value={k.qualified_7d} sub={k.qcpl_7d ? `QCPL ${inr(k.qcpl_7d)}` : "QCPL —"} />
          <Kpi label="Meet I this wk" value={k.meet_i_this_week} />
          <Kpi label="Proposed open" value={k.proposed_open} />
          <Kpi label="Won this month" value={k.won_this_month_count} sub={inr(k.won_this_month_value)} />
          <Kpi label="Replies waiting" value={k.replies_waiting_us} />
          <Kpi label="QCPL 7d" value={k.qcpl_7d ? inr(k.qcpl_7d) : "—"} />
        </div>

        {/* Grid: pipeline + today + replies + funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <section className="panel p-4 lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium">Pipeline by stage</h2>
              <span className="text-[10px] text-neutral-500">Zoho · Combined_Leads</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {data.pipeline.map((s) => (
                <div key={s.stage} className="panel-2 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">{s.stage}</div>
                  <div className="text-lg font-medium mt-1 font-mono">{s.count}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium mb-3">Funnel health</h2>
            <div className="space-y-1.5">
              {data.funnel_health.map((f) => (
                <div key={f.id} className="flex items-center justify-between text-xs">
                  <span className="text-neutral-300">{CONNECTOR_NAMES[f.id] || f.id}</span>
                  <StatusPill status={f.status} />
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <section className="panel p-4">
            <h2 className="text-sm font-medium mb-3">Replies tracker</h2>
            {data.replies.length === 0 ? <Empty label="Replies arrive in Phase 2" /> :
              <div className="space-y-2">{/* ... */}</div>}
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium mb-3">Today</h2>
            {data.today.length === 0 ? <Empty label="Connect Google Calendar in Settings" /> : null}
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium mb-3 flex items-center gap-2">
              Intel <span className="pill pill-ai">Claude · P3</span>
            </h2>
            <Empty label="Daily briefing arrives in Phase 3" />
          </section>
        </div>

        <section className="panel p-4">
          <h2 className="text-sm font-medium mb-3">Accountability (7d)</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
            {[
              ["Open asks", data.accountability_7d.open_asks ?? "—"],
              ["Median response", data.accountability_7d.median_response_h ? `${data.accountability_7d.median_response_h}h` : "—"],
              ["Completion", data.accountability_7d.completion_pct ? `${data.accountability_7d.completion_pct}%` : "—"],
              ["Rebook rate", data.accountability_7d.rebook_rate ?? "—"],
              ["Meet I show", data.accountability_7d.meet_i_show_rate ?? "—"],
              ["Prop→close", data.accountability_7d.proposal_to_close_pct ?? "—"],
            ].map(([l, v]) => (
              <div key={l} className="panel-2 p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500">{l}</div>
                <div className="text-sm font-mono mt-1">{v}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
